// Minimal DOM builder — the whole "framework" (ADR-0003).

type Child = Node | string | null | undefined;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | boolean | ((ev: Event) => void)> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (typeof value === 'function') {
      node.addEventListener(key.replace(/^on/, ''), value);
    } else if (typeof value === 'boolean') {
      if (value) node.setAttribute(key, '');
    } else {
      node.setAttribute(key, value);
    }
  }
  for (const child of children) {
    if (child == null) continue;
    node.append(child instanceof Node ? child : document.createTextNode(child));
  }
  return node;
}

/**
 * Create a custom element (`md-*`, ADR-0051). `el()` is typed to known HTML
 * tags, which Material's components are not — this is the same builder with
 * the tag name left open.
 */
export function ce(
  tag: string,
  attrs: Record<string, string | boolean> = {},
  ...children: Child[]
): HTMLElement {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === false) continue;
    node.setAttribute(key, value === true ? '' : value);
  }
  for (const child of children) {
    if (child == null) continue;
    node.append(child instanceof Node ? child : document.createTextNode(child));
  }
  return node;
}

/** An `md-*` control that carries a value (text field, select). */
export type ValueEl = HTMLElement & { value: string };
/** An `md-*-text-field`, which also takes focus and selects its contents. */
export type TextFieldEl = ValueEl & { select(): void; focus(): void };
/** An `md-filter-chip` — selection lives on the element, not on an input. */
export type ChipEl = HTMLElement & { selected: boolean };

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

/**
 * Member avatar: a colored disc with the member's initial. If the member
 * explicitly set an avatar string (e.g. an emoji), that is shown instead —
 * but the default look is typographic, not emoji.
 */
export function avatarEl(name: string, avatar: string, color: string, sizePx = 40): HTMLElement {
  const content = avatar.trim() !== '' ? avatar : (name.trim().charAt(0) || '?').toUpperCase();
  const node = el('span', { class: 'avatar', 'aria-hidden': 'true' }, content);
  node.style.width = `${sizePx}px`;
  node.style.height = `${sizePx}px`;
  node.style.fontSize = `${Math.round(sizePx * 0.45)}px`;
  node.style.background = color;
  return node;
}

export function fmtMinutes(minutes: number): string {
  const abs = Math.abs(minutes);
  if (abs < 60) return `${minutes} min`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const sign = minutes < 0 ? '-' : '';
  return m === 0 ? `${sign}${h} h` : `${sign}${h} h ${m} min`;
}
